// @flow
import Draggable from 'react-draggable';
import { Button, Position, Tooltip } from '@locus-taxy/neo-ui';
import * as React from 'react';
import theme from './styles.scss';
import { ScreenSizeStates } from 'common/components/core/ColumnResizer/ColumnResizeHelper';
import type { ScreenSizeStatesType } from 'common/components/core/ColumnResizer/ColumnResizeHelper';
import ResizerOverlay from './ResizerOverlay';
import { type TFunction } from 'react-i18next';

type Props = {
    screenSizeState: ScreenSizeStatesType,
    setScreenSizeState: ((string => string) | string) => void,
    onColumnResize: (e: $FixMe, data: $FixMe) => void,
    onColumnResizeStop: (e: $FixMe, data: $FixMe) => void,
    columnResizerPosition: { x: number, y: number },
    isResizing: boolean,
    nextScreenSizeState: ScreenSizeStatesType,
    t: TFunction
};
export const ColumnResizer = ({
    screenSizeState,
    setScreenSizeState,
    onColumnResize,
    onColumnResizeStop,
    columnResizerPosition,
    isResizing,
    nextScreenSizeState,
    t
}: Props): React.Node => {
    const onMinColumnWidth = React.useCallback(() => {
        setScreenSizeState(ScreenSizeStates.Min);
    }, [setScreenSizeState]);
    const onDefaultColumnWidth = React.useCallback(() => {
        setScreenSizeState(ScreenSizeStates.Default);
    }, [setScreenSizeState]);
    const onMaxColumnWidth = React.useCallback(() => {
        setScreenSizeState(ScreenSizeStates.Max);
    }, [setScreenSizeState]);

    const [isHovered, setIsHovered] = React.useState(false);

    const onMouseOver = React.useCallback(() => setIsHovered(true), []);
    const onMouseOut = React.useCallback(() => setIsHovered(false), []);

    return (
        <>
            <ResizerOverlay
                nextScreenSizeState={nextScreenSizeState}
                screenSizeState={screenSizeState}
                isResizing={isResizing}
            />
            <Draggable
                axis="x"
                cancel=".cancelDrag"
                onDrag={onColumnResize}
                onStop={onColumnResizeStop}
                position={columnResizerPosition}
                defaultClassNameDragging={theme.dragging}
            >
                <Position
                    position="absolute"
                    height="100%"
                    width="8px"
                    zIndex={25}
                    backgroundColor="white"
                    sx={{
                        boxShadow: '0 0 2px 0 rgba(0, 0, 0, 0.24)',
                        cursor: 'move'
                    }}
                    onMouseOver={onMouseOver}
                    onMouseOut={onMouseOut}
                >
                    <Position
                        position="absolute"
                        width={38}
                        height={96}
                        opacity={isResizing || isHovered ? 1 : 0}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="white"
                        left={
                            screenSizeState === ScreenSizeStates.Max
                                ? '100%'
                                : '50%'
                        }
                        top="50%"
                        sx={{
                            cursor: 'move',
                            borderRadius: '2px',
                            transform:
                                screenSizeState === ScreenSizeStates.Max
                                    ? 'translate(-100%, -50%)'
                                    : 'translate(-50%, -50%)',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.5)'
                        }}
                    >
                        <Tooltip
                            tooltip={t('columnResizer.largeMap')}
                            placement="left"
                        >
                            <Button
                                appearance="icon"
                                palette={
                                    screenSizeState === ScreenSizeStates.Min
                                        ? 'primary'
                                        : 'neutral'
                                }
                                icon="show_map"
                                onClick={onMinColumnWidth}
                            />
                        </Tooltip>
                        <Tooltip
                            tooltip={t('columnResizer.defaultMap')}
                            placement="left"
                        >
                            <Button
                                appearance="icon"
                                palette={
                                    screenSizeState === ScreenSizeStates.Default
                                        ? 'primary'
                                        : 'neutral'
                                }
                                icon="split_map"
                                onClick={onDefaultColumnWidth}
                            />
                        </Tooltip>
                        <Tooltip
                            tooltip={t('columnResizer.noMap')}
                            placement="left"
                        >
                            <Button
                                appearance="icon"
                                palette={
                                    screenSizeState === ScreenSizeStates.Max
                                        ? 'primary'
                                        : 'neutral'
                                }
                                icon="hide_map"
                                onClick={onMaxColumnWidth}
                            />
                        </Tooltip>
                    </Position>
                </Position>
            </Draggable>
        </>
    );
};
